// Gestione Docenti → SICUREZZA (Formazione Lavoratori)
// Colonne dinamiche dal foglio Excel; sotto-tab per GRUPPO (prima riga del foglio).
// Scadenza (anni) per colonna: le celle con data scaduta diventano rosse.
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

function parseDMY(s) {
  const m = String(s || "").trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
  if (!m) return null;
  const d = +m[1], mo = +m[2], y = +m[3];
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return dt;
}
function isExpired(dateStr, years) {
  const n = Number(years);
  if (!n || n <= 0) return false;
  const dt = parseDMY(dateStr);
  if (!dt) return false;
  const exp = new Date(dt.getFullYear() + n, dt.getMonth(), dt.getDate());
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return today > exp;
}

export default function DocentiSicurezzaPage() {
  const [rows, setRows] = useState([]);
  const [colonne, setColonne] = useState([]);
  const [tab, setTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/docenti-sicurezza");
      setRows(r.data.items || []);
      setColonne(r.data.colonne || []);
    } catch (e) {
      setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore caricamento" });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const g = [];
    colonne.forEach((c) => { if (c.gruppo && !g.includes(c.gruppo)) g.push(c.gruppo); });
    return g;
  }, [colonne]);

  useEffect(() => {
    if (groups.length && (!tab || !groups.includes(tab))) setTab(groups[0]);
  }, [groups, tab]);

  const cols = useMemo(() => colonne.filter((c) => c.gruppo === tab), [colonne, tab]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => `${r.nome || ""} ${r.ruolo || ""}`.toLowerCase().includes(s));
  }, [rows, search]);

  const saveCell = async (row, key, value) => {
    if ((row.valori?.[key] || "") === value) return;
    setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, valori: { ...r.valori, [key]: value } } : r));
    try { await api.put(`/docenti-sicurezza/${row.id}`, { valore_key: key, valore: value }); }
    catch (e) { setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore salvataggio" }); }
  };
  const saveField = async (row, field, value) => {
    if ((row[field] || "") === value) return;
    setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, [field]: value } : r));
    try { await api.put(`/docenti-sicurezza/${row.id}`, { [field]: value }); }
    catch (e) { setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore salvataggio" }); }
  };
  const saveScad = async (colKey, val) => {
    const next = colonne.map((c) => c.key === colKey ? { ...c, scad_anni: val === "" ? null : Number(val) } : c);
    setColonne(next);
    try { await api.put("/docenti-sicurezza-config", { colonne: next }); }
    catch (e) { setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore salvataggio scadenza" }); }
  };

  const onExport = async () => {
    try {
      const r = await api.get("/docenti-sicurezza/export", { params: tab ? { gruppo: tab } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `sicurezza_${(tab || "tutto").replace(/[^A-Za-z0-9]+/g, "_")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore export" }); }
  };

  const onPrint = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[m]));
    const oggi = new Date().toLocaleDateString("it-IT");
    const heads = cols.map((c) => `<th>${esc(c.label)}${c.scad_anni ? `<br><small>scad. ${c.scad_anni} anni</small>` : ""}</th>`).join("");
    const body = filtered.map((r, i) => {
      const tds = cols.map((c) => {
        const v = r.valori?.[c.key] || "";
        return `<td class="${isExpired(v, c.scad_anni) ? "red" : ""}">${esc(v)}</td>`;
      }).join("");
      return `<tr><td class="c">${i + 1}</td><td class="nome">${esc(r.nome)}</td><td>${esc(r.ruolo)}</td>${tds}</tr>`;
    }).join("");
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Sicurezza — ${esc(tab || "")}</title>
      <style>
        @page{size:A4 landscape;margin:10mm}
        *{font-family:Arial,Helvetica,sans-serif}
        body{margin:0;color:#263238}
        h1{font-size:16px;color:#0277bd;margin:0 0 2px}
        .sub{font-size:10px;color:#607d8b;margin:0 0 8px}
        table{width:100%;border-collapse:collapse;font-size:9px}
        th{background:#0277bd;color:#fff;padding:4px 3px;text-align:left;border:1px solid #0288d1}
        th small{font-weight:400;opacity:.85}
        td{padding:3px;border:1px solid #eceff1}
        td.c{text-align:center;color:#90a4ae} td.nome{font-weight:700;text-transform:uppercase}
        td.red{background:#c62828;color:#fff;font-weight:700}
        tr:nth-child(even) td{background:#f7fafc} tr:nth-child(even) td.red{background:#c62828}
      </style></head><body>
      <h1>🦺 Sicurezza — Formazione Lavoratori · ${esc(tab || "")}</h1>
      <p class="sub">Stampato il ${oggi} · ${filtered.length} persone</p>
      <table><thead><tr><th>#</th><th>Cognome Nome</th><th>Ruolo</th>${heads}</tr></thead><tbody>${body}</tbody></table>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { setMsg({ tipo: "err", testo: "Consenti i popup per stampare." }); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (_e) { /* noop */ } }, 350);
  };

  const addRow = async () => {
    const nome = window.prompt("Cognome e Nome della persona:");
    if (!nome || !nome.trim()) return;
    try { const r = await api.post("/docenti-sicurezza", { nome: nome.trim() }); setRows((rs) => [...rs, r.data]); }
    catch (e) { setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore creazione" }); }
  };
  const delRow = async (row) => {
    if (!window.confirm(`Eliminare la riga di ${row.nome}?`)) return;
    try { await api.delete(`/docenti-sicurezza/${row.id}`); setRows((rs) => rs.filter((r) => r.id !== row.id)); }
    catch (e) { setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore eliminazione" }); }
  };

  const th = { padding: "6px 6px", textAlign: "left", position: "sticky", top: 0, background: "#0277bd", color: "white", fontSize: 10, whiteSpace: "nowrap", borderRight: "1px solid #0288d1" };
  const inp = { width: "100%", border: "1px solid transparent", background: "transparent", fontSize: 11, padding: "3px 4px", borderRadius: 2 };

  return (
    <div data-testid="docenti-sicurezza-page" style={{ padding: "12px 18px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: "#0277bd" }}>🦺 Sicurezza — Formazione Lavoratori</h2>
        <input data-testid="sic-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Cerca nome / ruolo…" style={{ padding: "6px 10px", border: "1px solid #cfd8dc", fontSize: 12, borderRadius: 3, width: 220 }} />
        <button data-testid="sic-add" onClick={addRow} style={{ padding: "6px 12px", background: "#2e7d32", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>➕ Aggiungi riga</button>
        <button data-testid="sic-export-btn" onClick={onExport} disabled={!rows.length}
          style={{ padding: "6px 12px", background: "#1b7e3c", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>📊 Esporta Excel</button>
        <button data-testid="sic-print-btn" onClick={onPrint} disabled={!rows.length}
          style={{ padding: "6px 12px", background: "#455a64", color: "white", border: "none", borderRadius: 3, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>🖨 Stampa</button>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#607d8b" }}><b>{filtered.length}</b> persone{search ? ` (di ${rows.length})` : ""}</span>
      </div>

      {msg && <div data-testid="sic-msg" style={{ marginBottom: 8, padding: "8px 12px", borderRadius: 4, fontSize: 12,
        background: msg.tipo === "ok" ? "#e8f5e9" : "#ffebee", color: msg.tipo === "ok" ? "#1b5e20" : "#b71c1c",
        border: `1px solid ${msg.tipo === "ok" ? "#a5d6a7" : "#ef9a9a"}` }}>{msg.testo}</div>}

      {/* Sotto-tab per GRUPPO (prima riga del foglio) */}
      {groups.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8, borderBottom: "2px solid #0277bd" }}>
          {groups.map((g) => (
            <button key={g} data-testid={`sic-tab-${g}`} onClick={() => setTab(g)}
              style={{ padding: "7px 16px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                borderRadius: "5px 5px 0 0", background: tab === g ? "#0277bd" : "#e1f5fe", color: tab === g ? "white" : "#0277bd" }}>
              {g} <span style={{ opacity: 0.75, fontWeight: 400 }}>({colonne.filter((c) => c.gruppo === g).length})</span>
            </button>
          ))}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div data-testid="sic-empty" style={{ padding: 30, textAlign: "center", color: "#90a4ae", border: "1px dashed #cfd8dc", borderRadius: 6 }}>
          Nessun dato nella tabella Sicurezza. Usa “➕ Aggiungi riga” per inserire le persone.
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ border: "1px solid #cfd8dc", borderRadius: 4, overflow: "auto", maxHeight: "72vh" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11, minWidth: 700 }} data-testid="sic-table">
            <thead>
              <tr>
                <th style={{ ...th, width: 34 }}>#</th>
                <th style={{ ...th, minWidth: 190, left: 0 }}>Cognome Nome</th>
                <th style={{ ...th, minWidth: 130 }}>Ruolo</th>
                {cols.map((c) => (
                  <th key={c.key} style={{ ...th, minWidth: 100 }}>
                    <div title={c.label}>{c.label}</div>
                    <div style={{ marginTop: 3, display: "flex", alignItems: "center", gap: 3 }}>
                      <span style={{ fontSize: 9, opacity: 0.85, fontWeight: 400 }}>scad.</span>
                      <input data-testid={`sic-scad-${c.key}`} type="number" min="0" value={c.scad_anni ?? ""}
                        onChange={(e) => { const val = e.target.value; setColonne((cs) => cs.map((x) => x.key === c.key ? { ...x, scad_anni: val === "" ? null : Number(val) } : x)); }}
                        onBlur={(e) => saveScad(c.key, e.target.value)} title="Anni di validità: le celle scadute diventano rosse"
                        style={{ width: 34, fontSize: 10, padding: "1px 2px", borderRadius: 2, border: "none", textAlign: "center", color: "#263238", background: "#fff" }} />
                      <span style={{ fontSize: 9, opacity: 0.85, fontWeight: 400 }}>anni</span>
                    </div>
                  </th>
                ))}
                <th style={{ ...th, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr key={row.id} data-testid={`sic-row-${i}`} style={{ borderBottom: "1px solid #eceff1", background: i % 2 ? "#f7fafc" : "white" }}>
                  <td style={{ padding: "3px 6px", textAlign: "center", color: "#90a4ae", fontFamily: "monospace" }}>{i + 1}</td>
                  <td style={{ padding: "2px 4px", position: "sticky", left: 0, background: i % 2 ? "#f7fafc" : "white", borderRight: "1px solid #eceff1" }}>
                    <input data-testid={`sic-nome-${i}`} defaultValue={row.nome || ""} onBlur={(e) => saveField(row, "nome", e.target.value)}
                      style={{ ...inp, fontWeight: 700, textTransform: "uppercase" }} />
                  </td>
                  <td style={{ padding: "2px 4px", borderRight: "1px solid #f0f0f0" }}>
                    <input data-testid={`sic-ruolo-${i}`} defaultValue={row.ruolo || ""} onBlur={(e) => saveField(row, "ruolo", e.target.value)} style={inp} />
                  </td>
                  {cols.map((c) => {
                    const val = row.valori?.[c.key] || "";
                    const expired = isExpired(val, c.scad_anni);
                    return (
                      <td key={c.key} data-testid={`sic-cell-${c.key}-${i}`}
                        style={{ padding: "2px 4px", borderRight: "1px solid #f0f0f0", background: expired ? "#c62828" : undefined }}>
                        <input data-testid={`sic-${c.key}-${i}`} defaultValue={val} onBlur={(e) => saveCell(row, c.key, e.target.value)}
                          style={{ ...inp, color: expired ? "white" : undefined, fontWeight: expired ? 700 : undefined }} />
                      </td>
                    );
                  })}
                  <td style={{ padding: "2px 4px", textAlign: "center" }}>
                    <button data-testid={`sic-del-${i}`} onClick={() => delRow(row)} title="Elimina riga"
                      style={{ background: "transparent", border: "none", color: "#e53935", cursor: "pointer", fontSize: 14 }}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
